import React, { useState } from 'react';
import { SELECTORS } from '@oneaudi/stck-store';
import { useSelector } from 'react-redux';
import { useI18n } from '@oneaudi/i18n-context';
import {
  useServicesContext,
  useClientServerUtils,
  getActiveFiltersCount,
  useDesktopOrMobileView,
  DynamicComponent,
} from '@oneaudi/vtp-shared';
import { LayerHandleV1 } from '@volkswagen-onehub/gfa-layer-manager';
import { LayerManagerV25 } from '@volkswagen-onehub/layer-manager';
import ReactDOM from 'react-dom';
import { QuickFilters } from '../quickFilters/subcomponents/QuickFilters';
import { ContentService } from '@oneaudi/content-service';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { trackBackLinkClick } from '../../tracking/tilesTracking';
import {
  FavsAndLocWrapper,
  ResposiveFilterBtnContainer,
  StyledContainer,
  StyledToolButton,
  StyledCount,
  StyledTextButton,
} from './styles/Toolbar.styles';
import { trackFilterLayerOnMobile } from '../../tracking/quickFiltersTracking';
import { PlpConfiguration } from '../../hooks/tileHook';

export interface ToolbarProps {}

const Toolbar: React.FC<ToolbarProps> = () => {
  const favouritesLabel = useI18n({
    id: `nemo.sc.filter.toolbarFavourites`,
    defaultMessage: 'Favourites',
  });

  const favouritesArealabel = useI18n({
    id: 'stockcars.toolbar.favourites.arialabel',
    defaultMessage: '',
  });
  const backButtonLabel = useI18n({
    id: `nemo.ui.sc.favorites.empty.back`,
    defaultMessage: 'Back',
  });
  const amountOfFavouriteVehicles = useSelector((state) =>
    SELECTORS.VEHICLES.getFavoriteVehicleIdsState(state)
  ) as string[];

  const filterLabel = useI18n({
    id: `nemo.sc.filter.toolbarFilter`,
    defaultMessage: 'Filter',
  });
  const isMobile: boolean = useDesktopOrMobileView('767px') === 'mobile';
  const layerManager = useServicesContext().getAdditionalService(
    'layer-manager'
  ) as LayerManagerV25;
  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;
  const [quickFiltersLayerNode, setQuickFiltersLayerNode] = useState<Element>();
  const [quickFiltersLayer, setQuickFiltersLayer] = useState<LayerHandleV1<{}>>();
  const quickFiltersLayerRef = React.useCallback(
    (node: any) => {
      setQuickFiltersLayerNode(node);
    },
    [setQuickFiltersLayerNode]
  );
  const openOverlay = () => {
    const layer = layerManager.openFullscreenLayer(
      () => <div ref={quickFiltersLayerRef} />,
      {},
      {
        onClose: () => handleCloseQuickFiltersOverlay(),
      }
    );
    setQuickFiltersLayer(layer);
  };

  const handleCloseQuickFiltersOverlay = () => {
    quickFiltersLayer?.close();
  };
  const handleFilterBtnClick = () => {
    openOverlay();
    trackFilterLayerOnMobile(trackingService);
  };

  const activeFiltersCount = getActiveFiltersCount();

  const { isClient } = useClientServerUtils();
  const handleBackBtnClick = () => {
    if (!isClient) {
      // es-lint-disable-next-line no-console
      console.info('Cannot navigate back during SSR.');
      return;
    }
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
    trackBackLinkClick(trackingService, backButtonLabel);
  };

  const audiContentService = useServicesContext().getAdditionalService(
    'audi-content-service'
  ) as ContentService;
  const { favouritesPagePathname, appContext, vtpConfiguration } = audiContentService?.getContent()
    ?.fields as PlpConfiguration;
  const { enableMyAudiWishlist } = vtpConfiguration?.fields ?? {};

  const isFavorites = appContext === 'favorites';

  return (
    <StyledContainer isFavorites={isFavorites}>
      <>
        {/* Back Button only shown in favorites */}
        {isFavorites && (
          <StyledToolButton
            variant="tertiary"
            icon="back"
            iconPosition="leading"
            size="sm"
            onClick={handleBackBtnClick}
            data-testid="toolbarBackBtn"
          >
            {backButtonLabel}
          </StyledToolButton>
        )}
        <FavsAndLocWrapper>
          {/* Filter Option */}
          {appContext === 'results' && (
            <ResposiveFilterBtnContainer>
              <DynamicComponent
                legacy={
                  <>
                    <StyledToolButton
                      variant="tertiary"
                      icon="options"
                      iconPosition="leading"
                      onClick={handleFilterBtnClick}
                      size="sm"
                      data-testid="toolbarFilterBtn"
                    >
                      {filterLabel}
                    </StyledToolButton>

                    {activeFiltersCount > 0 && (
                      <StyledCount count={activeFiltersCount} data-testid="toolbarFilterCount" />
                    )}
                  </>
                }
                alpha={
                  <>
                    <StyledToolButton
                      variant="tertiary"
                      icon="options"
                      iconPosition="leading"
                      onClick={handleFilterBtnClick}
                      size="sm"
                    >
                      {filterLabel}
                      {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
                    </StyledToolButton>
                  </>
                }
              />
            </ResposiveFilterBtnContainer>
          )}
          {/* Favorites */}
          {appContext === 'results' && !enableMyAudiWishlist && (
            <DynamicComponent
              legacy={
                <StyledTextButton
                  variant="tertiary"
                  icon="favorite"
                  iconPosition="leading"
                  href={favouritesPagePathname}
                  size="sm"
                  data-testid="toolbarFavorites"
                  aria-label={favouritesArealabel}
                >
                  {!isMobile && favouritesLabel}
                  {!isMobile && amountOfFavouriteVehicles.length > 0 && (
                    <StyledCount
                      count={amountOfFavouriteVehicles.length}
                      labelPosition="leading"
                      data-testid="toolbarFavoritesCount"
                    />
                  )}
                </StyledTextButton>
              }
              alpha={
                <StyledTextButton
                  variant="tertiary"
                  icon="favorite"
                  iconPosition="leading"
                  href={favouritesPagePathname}
                  size="sm"
                  aria-label={favouritesArealabel}
                >
                  {!isMobile && favouritesLabel}
                  {!isMobile &&
                    amountOfFavouriteVehicles.length > 0 &&
                    ` (${amountOfFavouriteVehicles.length})`}
                </StyledTextButton>
              }
            />
          )}
        </FavsAndLocWrapper>
        {quickFiltersLayerNode &&
          ReactDOM.createPortal(
            <QuickFilters handleCloseQuickFiltersOverlay={handleCloseQuickFiltersOverlay} />,
            quickFiltersLayerNode!
          )}
      </>
    </StyledContainer>
  );
};

export default Toolbar;
